import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Briefcase, Languages, Moon, Sun } from 'lucide-react';
import { useLanguage } from '@/LanguageContext';
import { getTranslation } from '@/translations';
import { useTheme } from '@/ThemeContext';

const Login = ({ onLogin }) => {
  const { language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const t = (key) => getTranslation(key, language);
  
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, {
        username,
        password,
      });

      toast.success(isLogin ? t('login_successful') : t('account_created'));
      onLogin(response.data.access_token, response.data.username, response.data.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-800" data-testid="login-page">
      {/* Language and Theme Toggles */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <Button
          onClick={toggleTheme}
          variant="outline"
          className="dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700"
          title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
        >
          {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
        </Button>
        <Button
          onClick={toggleLanguage}
          variant="outline"
          className="dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700"
          title={language === 'en' ? 'Switch to Spanish' : 'Cambiar a Inglés'}
        >
          <Languages className="w-4 h-4 mr-2" />
          {language === 'en' ? 'ES' : 'EN'}
        </Button>
      </div>
      
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <div className="bg-gradient-to-br from-sky-400 to-blue-500 p-3 rounded-2xl shadow-lg">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold ml-4 bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
            JobTracker Pro
          </h1>
        </div>

        <Card className="shadow-xl border-0 backdrop-blur-sm bg-white/90 dark:bg-slate-800/90 dark:text-slate-200">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-center dark:text-slate-100">
              {isLogin ? t('welcome_back') : t('create_account')}
            </CardTitle>
            <CardDescription className="text-center">
              {isLogin ? t('sign_in') : t('sign_up')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t('username')}</Label>
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  placeholder={t('username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder={t('password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <Button
                data-testid="submit-button"
                type="submit"
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium py-2"
                disabled={loading}
              >
                {loading ? 'Please wait...' : (isLogin ? t('sign_in_button') : t('sign_up_button'))}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm">
              <button
                data-testid="toggle-auth-mode"
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sky-600 hover:text-sky-700 font-medium"
              >
                {isLogin ? t('dont_have_account') : t('already_have_account')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
